import { t } from "@lingui/macro";
import { useAtom, useSetAtom } from "jotai";
import { useEffect } from "react";

import { setRequestedTimeAtom } from "@/features/BookingWizard/atoms/actions/setRequestedTime/setRequestedTimeAtom";
import { BookingWizardDataAtom } from "@/features/BookingWizard/atoms/stores";
import {
  useGetEarlyTimeOptions,
  useGetLateTimeOptions,
  useGetTimeOptions,
} from "@/features/BookingWizard/hooks/useGetTimeOptions/useGetTimeOptions";
import { useFilterRequestStartTimeSlots } from "@/hooks/useFilterRequestStartTimeSlots/useFilterRequestStartTimeSlots";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

type TimeSlotNode = {
  readonly id: string;
  readonly time: string;
  readonly availability?: string | null;
} | null;

type Props = {
  timeSlots: TimeSlotNode[];
};

/**
 * Component for selecting the time options in a request booking
 */
export const TimeSelectionControl = ({ timeSlots }: Props) => {
  const [bookingWizardData, setBookingWizardData] = useAtom(
    BookingWizardDataAtom
  );
  const setRequestedTime = useSetAtom(setRequestedTimeAtom);

  const availableTimeSlots = useFilterRequestStartTimeSlots(timeSlots);
  const timeOptions = useGetTimeOptions(availableTimeSlots);

  const isRequestWizardBooking =
    bookingWizardData?.type === "RequestBooking" ||
    bookingWizardData?.type === "LessonBooking";

  const requestedTime = isRequestWizardBooking
    ? bookingWizardData.requestedTime || ""
    : "";

  const earlyAsOptions = useGetEarlyTimeOptions(timeOptions, requestedTime);
  const lateAsOptions = useGetLateTimeOptions(timeOptions, requestedTime);

  useEffect(() => {
    if (!isRequestWizardBooking || !requestedTime || timeOptions.length === 0) {
      return;
    }

    const requestedTimeIsAvailable = timeOptions.some(
      (option) => option.value === requestedTime
    );

    if (requestedTimeIsAvailable) {
      return;
    }

    const nextRequestedTime = timeOptions[0]?.value;
    if (!nextRequestedTime) {
      return;
    }

    setRequestedTime({
      requestedTime: nextRequestedTime,
      minTime: bookingWizardData.minTime || nextRequestedTime,
      maxTime:
        bookingWizardData.maxTime ||
        timeOptions[timeOptions.length - 1]?.value ||
        nextRequestedTime,
    });
  }, [
    bookingWizardData,
    isRequestWizardBooking,
    requestedTime,
    setRequestedTime,
    timeOptions,
  ]);

  if (!isRequestWizardBooking) {
    return null;
  }

  const minTime = bookingWizardData.minTime || "";
  const maxTime = bookingWizardData.maxTime || "";

  return (
    <VStack pt={2} gap={2}>
      <VStack
        p={5}
        pb={2}
        gap={4}
        flex={1}
        bg="surface-primary"
        borderRadius="4"
      >
        <Text variant="mainBaseSemiBold">{t`Start Time`}</Text>
        <DropdownSelect
          value={requestedTime}
          placeholder={t`Select a time`}
          options={timeOptions}
          testID="requested-time-select"
          onChange={(val) => {
            if (val) {
              const minTime = bookingWizardData.minTime
                ? bookingWizardData.minTime <= val
                  ? bookingWizardData.minTime
                  : val
                : timeOptions[0]?.value || val;
              const maxTime = bookingWizardData.maxTime
                ? bookingWizardData.maxTime >= val
                  ? bookingWizardData.maxTime
                  : val
                : timeOptions[timeOptions.length - 1]?.value || val;

              setRequestedTime({
                requestedTime: val,
                minTime,
                maxTime,
              });
            }
          }}
        />
      </VStack>

      <HStack gap={4} p={5} pb={2} bg="surface-primary" borderRadius="4">
        <VStack gap={4} flex={1}>
          <Text variant="mainBaseSemiBold" color="text-primary">
            {t`I can start as early as ...`}
          </Text>
          <DropdownSelect
            value={minTime}
            placeholder={t`Select a time`}
            options={earlyAsOptions}
            testID="request-early-time-select"
            onChange={(val) => {
              if (val) {
                setBookingWizardData({
                  ...bookingWizardData,
                  minTime: val,
                });
              }
            }}
          />
        </VStack>

        <VStack gap={4} flex={1}>
          <Text variant="mainBaseSemiBold" color="text-primary">
            {t`... and as late as`}
          </Text>
          <DropdownSelect
            value={maxTime}
            placeholder={t`Select a time`}
            options={lateAsOptions}
            testID="request-late-time-select"
            onChange={(val) => {
              if (val) {
                setBookingWizardData({
                  ...bookingWizardData,
                  maxTime: val,
                });
              }
            }}
          />
        </VStack>
      </HStack>
    </VStack>
  );
};
